/**
 * The single source of truth for Haven's own browser-extension IDs (ENG-2435).
 *
 * These are the IDs Chrome/Edge assign to the builds we ship. Do NOT hand-copy these strings
 * elsewhere — import from here (that is the whole point of this ticket: one home, not four). A new
 * store listing (e.g. Edge Add-ons) mints another ID; add it here and every consumer picks it up.
 */
export const HAVEN_EXTENSION_IDS = {
  /** Chrome Web Store production listing. */
  PROD: "becohfjpbibfjlfeiphcjecdjbdhomgi",
  /** Haven Canary (speculative next-minor pre-release, served via update.xml). */
  CANARY: "aggfmchpkkkijddhfjbnjledllpaappg",
  /**
   * Haven Edge Canary (ENG-2439) — the Edge-track counterpart of CANARY. Derived from a
   * dedicated key committed in `packages/haven-extension/manifest.edge.json` so every
   * `build-edge.sh` run produces this same id regardless of machine/unzip path, instead of
   * Chromium's default of deriving an id from the unpacked folder path. NOT Haven's real
   * published Edge Add-ons id — Edge Add-ons mints its own on submission and ignores this key
   * (see docs/EDGE_EXTENSION_PORT.md); this one is for the continuous Edge canary build
   * (`build:extension:edge:canary`) and its own `extension-edge-v*` release track.
   */
  EDGE_CANARY: "mgemimebpooappnhbgmfhhkbfkpdhhib",
  /**
   * Microsoft Edge Add-ons production listing (ENG-3911). Minted by Partner Center on submission
   * (product id 687c52bc-7486-4d4f-be4d-f9cdab9de2c0, store id 0RDCKB79PZQ1); Edge Add-ons does
   * not honor a pinned key, so this differs from PROD. See docs/EDGE_EXTENSION_PORT.md.
   */
  EDGE: "lknjllnfbbekidakodagniianbaonlnn",
} as const;

/** Analytics environment tag derived from the running extension's build. */
export type HavenExtensionEnv = "prod" | "canary" | "edge" | "edge-canary" | "dev";

/**
 * Maps a `chrome.runtime.id` to its analytics environment tag (ENG-2435). Anything that is not a
 * known shipped build (a local/unpacked/keyed dev build) reports `"dev"`.
 */
export function havenExtensionEnvForId(
  id: string | null | undefined
): HavenExtensionEnv {
  if (id === HAVEN_EXTENSION_IDS.PROD) return "prod";
  if (id === HAVEN_EXTENSION_IDS.CANARY) return "canary";
  // ENG-3911: its own tag, not "prod" — the backend maps env to an extension id
  // (HavenExtensionBuilds.envToIdSql), and "prod" would list Edge installs under the Chrome id.
  if (id === HAVEN_EXTENSION_IDS.EDGE) return "edge";
  if (id === HAVEN_EXTENSION_IDS.EDGE_CANARY) return "edge-canary";
  return "dev";
}

/** The set of extension IDs that are Haven's own shipped builds (prod + canary). */
export const HAVEN_OWN_EXTENSION_IDS: ReadonlySet<string> = new Set(
  Object.values(HAVEN_EXTENSION_IDS)
);

/**
 * `chrome-extension://<id>/` origins for the desktop app's native-messaging `allowed_origins`
 * (ENG-2435 Bug 2). Includes prod + canary so a canary build can also talk to the desktop app.
 */
export const HAVEN_EXTENSION_NATIVE_MESSAGING_ORIGINS: readonly string[] =
  Object.values(HAVEN_EXTENSION_IDS).map((id) => `chrome-extension://${id}/`);

/** Chrome Web Store listing URL for the Haven production extension. */
export const CHROME_WEB_STORE_URL = `https://chromewebstore.google.com/detail/haven/${HAVEN_EXTENSION_IDS.PROD}`;

/**
 * Microsoft Edge Add-ons listing URL (ENG-3911). Resolves only once the listing is published;
 * install CTAs are not wired to it yet.
 */
export const MICROSOFT_EDGE_ADDONS_URL = `https://microsoftedge.microsoft.com/addons/detail/haven/${HAVEN_EXTENSION_IDS.EDGE}`;
